import { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { categories, animeArticles, siteInfo } from '@/mocks/anime';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const [activeSlug, setActiveSlug] = useState(slug || 'rexue');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 9;

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      setCurrentPage(1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [slug]);

  const activeCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug),
    [activeSlug]
  );

  const filteredArticles = useMemo(() => {
    let articles = animeArticles;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      articles = articles.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.description.toLowerCase().includes(q) ||
          a.tags.some((t) => t.toLowerCase().includes(q))
      );
    } else {
      articles = articles.filter((a) => a.category === activeSlug);
    }
    return articles;
  }, [activeSlug, searchQuery]);

  const totalPages = Math.ceil(filteredArticles.length / pageSize);
  const paginatedArticles = filteredArticles.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const pageTitle = searchQuery
    ? `搜索: ${searchQuery}`
    : activeCategory
    ? `${activeCategory.name}动漫 - 动漫大全`
    : '动漫分类';

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: siteInfo.url,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: activeCategory ? `${activeCategory.name}动漫` : '动漫分类',
        item: `${siteInfo.url}/category/${activeSlug}`,
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={
          activeCategory
            ? `${activeCategory.description}，樱花动漫网为您精选${activeCategory.name}动漫大全，手机端随时随地畅享${activeCategory.name}动漫的精彩世界。`
            : '樱花动漫网动漫分类大全，按类型浏览热血、恋爱、搞笑、奇幻等各类动漫作品。'
        }
        keywords={`${activeCategory ? activeCategory.name : ''}动漫,手机动漫,动漫大全${activeCategory ? `,${activeCategory.name}动漫推荐` : ''}`}
        canonicalUrl={`${siteInfo.url}/category/${activeSlug}`}
        jsonLd={breadcrumbSchema}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          {/* Breadcrumb */}
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-4">
            <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </span>
              {searchQuery ? (
                <>
                  <span className="text-foreground-400">搜索</span>
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line"></i>
                  </span>
                  <span className="text-foreground-700">{searchQuery}</span>
                </>
              ) : (
                <span className="text-foreground-700">{activeCategory?.name}动漫</span>
              )}
            </nav>
          </div>

          {/* Category Tabs */}
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 mb-6">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  onClick={() => {
                    setActiveSlug(cat.slug);
                    setCurrentPage(1);
                  }}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <span className={`w-4 h-4 flex items-center justify-center ${activeSlug === cat.slug ? '' : ''}`}>
                    <i className={`${cat.icon} text-xs`}></i>
                  </span>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Results */}
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 pb-16">
            {searchQuery && (
              <div className="mb-6">
                <h1 className="text-xl font-heading font-bold text-foreground-950">
                  搜索 &ldquo;{searchQuery}&rdquo; 的结果
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  共找到 {filteredArticles.length} 部相关动漫
                </p>
              </div>
            )}

            {!searchQuery && activeCategory && (
              <div className="mb-8">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {activeCategory.name}动漫
                </h1>
                <p className="text-sm text-foreground-600 mt-2">
                  {activeCategory.description}
                </p>
              </div>
            )}

            {paginatedArticles.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {paginatedArticles.map((anime) => (
                    <Link
                      key={anime.id}
                      to={`/article/${anime.id}`}
                      className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 hover:-translate-y-0.5 cursor-pointer"
                    >
                      <div className="flex gap-4 p-4">
                        <div className="w-24 h-32 flex-shrink-0 rounded-lg overflow-hidden">
                          <img
                            src={anime.coverImage}
                            alt={anime.title}
                            title={`${anime.title} - 樱花动漫`}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                        <div className="flex-1 min-w-0 flex flex-col justify-between">
                          <div>
                            <h3 className="text-base font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                              {anime.title}
                            </h3>
                            <p className="text-xs text-foreground-500 mt-1.5 line-clamp-2 leading-relaxed">
                              {anime.description}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap mt-2">
                            <span className="text-xs px-1.5 py-0.5 bg-primary-100 text-primary-700 rounded font-medium whitespace-nowrap">
                              {anime.categoryName}
                            </span>
                            <span className="text-xs text-foreground-400">{anime.episodes}</span>
                            <span className="text-xs text-foreground-400">·</span>
                            <span className="text-xs text-accent-500 font-medium">{anime.rating}分</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-10">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200 text-foreground-600 hover:border-primary-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                      <button
                        key={p}
                        onClick={() => setCurrentPage(p)}
                        className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                          p === currentPage
                            ? 'bg-primary-500 text-background-50'
                            : 'border border-background-200 text-foreground-600 hover:border-primary-300'
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200 text-foreground-600 hover:border-primary-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center mb-4 text-foreground-300">
                  <i className="ri-emotion-sad-line text-3xl"></i>
                </div>
                <p className="text-foreground-500">没有找到相关动漫</p>
                <Link
                  to="/"
                  className="inline-block mt-4 text-sm text-primary-500 hover:text-primary-600 cursor-pointer whitespace-nowrap"
                >
                  返回首页
                </Link>
              </div>
            )}
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}